import {
  Context,
  createContext,
  ReactNode,
  useCallback,
  useEffect,
  useState,
} from "react";

import dayjs from "dayjs";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import { useSearchParams } from "next/navigation";
import Script from "next/script";

dayjs.extend(utc);
dayjs.extend(timezone);

export const SALESFORCE_CHAT_START = 8; // 8 am.
export const SALESFORCE_CHAT_END = 20; // 8 pm.

export interface SalesforceConfig {
  /**
   * Salesforce chat key/ID
   */
  key: string;
  /**
   * Salesforce site endpoint URL
   */
  siteEndpoint: string;
  /**
   * Salesforce SCRT2 URL
   */
  scrt2Url: string;
  /**
   * Label for the messaging session (e.g., "Pre_Authentication_Messaging_Session")
   */
  sessionLabel: string;
  /**
   * Website identifier for Salesforce pre-chat fields
   */
  websiteLabel: string;
  /**
   * Session storage key for tracking active chat state
   */
  chatActiveKey: string;
}

// Interface for the Salesforce based Embedded Chat Service.
export interface EmbeddedChatService {
  init(
    id: string,
    label: string,
    messagingUrl: string,
    options: {
      scrt2URL: string;
    },
  ): void;
  prechatAPI: {
    setHiddenPrechatFields(options: { Website: string }): void;
  };
  settings: {
    hideChatButtonOnLoad: boolean;
    language: string;
  };
  utilAPI: {
    launchChat: () => Promise<void>;
  };
}

declare global {
  interface Window {
    embeddedservice_bootstrap?: EmbeddedChatService;
  }
}

export interface SalesforceChatSupportContextValue {
  isChatReady: boolean;
  launchChat: () => void;
}

export const SalesforceChatSupportContext = createContext<
  SalesforceChatSupportContextValue | undefined
>(undefined);

interface SalesforceChatSupportProps {
  children: ReactNode;
  /**
   * React Context to use for this provider instance.
   * This allows multiple independent chat instances to coexist.
   */
  context: Context<SalesforceChatSupportContextValue | undefined>;
  /**
   * Salesforce configuration (required).
   * Use LoginSalesforceChatSupport or AuthenticatedSalesforceChatSupport
   * wrappers for specific defaults.
   */
  config: SalesforceConfig;
}

export const SalesforceChatSupport = ({
  children,
  context,
  config,
}: SalesforceChatSupportProps) => {
  const hasRequiredConfig = Boolean(
    config.key &&
      config.siteEndpoint &&
      config.scrt2Url &&
      config.sessionLabel &&
      config.websiteLabel,
  );
  const [isChatLoaded, setIsChatLoaded] = useState(false);
  const [isChatReady, setIsChatReady] = useState(false);
  const params = useSearchParams();

  const initEmbeddedMessaging = useCallback(() => {
    try {
      if (window.embeddedservice_bootstrap) {
        window.embeddedservice_bootstrap.settings.language = "en_US";
        window.embeddedservice_bootstrap.settings.hideChatButtonOnLoad = true;

        window.embeddedservice_bootstrap.init(
          config.key,
          config.sessionLabel,
          config.siteEndpoint,
          {
            scrt2URL: config.scrt2Url,
          },
        );

        setIsChatLoaded(true);
      } else {
        console.error("Did not see Salesforce embeddedservice_bootstrap");
      }
    } catch (err) {
      console.error("Error loading Salesforce Embedded Messaging: ", err);
    }
  }, [config]);

  const launchChat = useCallback(() => {
    if (window.embeddedservice_bootstrap) {
      if (window.embeddedservice_bootstrap.utilAPI) {
        window.embeddedservice_bootstrap.utilAPI.launchChat().catch((error) => {
          console.error("Failed to launch Salesforce chat client: ", error);
        });
      } else {
        console.error(
          "Salesforce embeddedservice_bootstrap.utilAPI is not available",
        );
      }
    } else {
      console.error("Salesforce embeddedservice_bootstrap is not defined");
    }
  }, []);

  useEffect(() => {
    if (params?.get("officeHours") === "false") {
      setIsChatReady(false);
    }

    if (!isChatLoaded) {
      return;
    }

    const handleWithinOfficeHours = () => {
      if (params?.get("officeHours") === "false") {
        setIsChatReady(false);
        return;
      }
      setIsChatReady(true);
    };

    const handleOutsideOfficeHours = () => {
      setIsChatReady(false);
    };

    const handleMessagingReady = () => {
      window.embeddedservice_bootstrap?.prechatAPI.setHiddenPrechatFields({
        Website: config.websiteLabel,
      });

      if (params?.get("officeHours") === "false") {
        setIsChatReady(false);
        return;
      }

      // The support hours are in EST (aka New York).
      const now = dayjs().tz("America/New_York");
      const dayOfWeek = now.day();
      if (dayOfWeek < 1 || dayOfWeek > 5) {
        // eslint-disable-next-line no-console
        console.info("Salesforce chat is not available today.");
        return;
      }

      const startChatHour = now.hour(SALESFORCE_CHAT_START).hour();
      const endChatHour = now.hour(SALESFORCE_CHAT_END).hour();
      const currentHour = now.hour();

      // M - F 8 - 8
      if (currentHour >= startChatHour && currentHour <= endChatHour) {
        setIsChatReady(true);

        // Check if we need to restore a chat from previous page
        if (sessionStorage.getItem(config.chatActiveKey)) {
          sessionStorage.removeItem(config.chatActiveKey);
          // Small delay to ensure Salesforce is fully ready
          setTimeout(() => {
            window.embeddedservice_bootstrap?.utilAPI
              ?.launchChat()
              .catch((err) => {
                console.error("Failed to restore chat:", err);
              });
          }, 100);
        }
      } else {
        // eslint-disable-next-line no-console
        console.info(
          "Salesforce chat is outside of office hours on initialization.",
        );
      }
    };

    const handleConversationStarted = () => {
      sessionStorage.setItem(config.chatActiveKey, "true");
    };

    window.addEventListener(
      "onEmbeddedMessagingWithinBusinessHours",
      handleWithinOfficeHours,
    );
    window.addEventListener(
      "onEmbeddedMessagingBusinessHoursStarted",
      handleWithinOfficeHours,
    );
    window.addEventListener(
      "onEmbeddedMessagingBusinessHoursEnded",
      handleOutsideOfficeHours,
    );
    window.addEventListener("onEmbeddedMessagingReady", handleMessagingReady);
    window.addEventListener(
      "onEmbeddedMessagingConversationStarted",
      handleConversationStarted,
    );

    // Clean listeners when unmounted.
    return () => {
      window.removeEventListener(
        "onEmbeddedMessagingWithinBusinessHours",
        handleWithinOfficeHours,
      );
      window.removeEventListener(
        "onEmbeddedMessagingBusinessHoursStarted",
        handleWithinOfficeHours,
      );
      window.removeEventListener(
        "onEmbeddedMessagingBusinessHoursEnded",
        handleOutsideOfficeHours,
      );
      window.removeEventListener(
        "onEmbeddedMessagingReady",
        handleMessagingReady,
      );
      window.removeEventListener(
        "onEmbeddedMessagingConversationStarted",
        handleConversationStarted,
      );
    };
  }, [isChatLoaded, params, config]);

  const ContextProvider = context.Provider;

  return (
    <ContextProvider value={{ isChatReady, launchChat }}>
      {hasRequiredConfig && (
        <Script
          id="salesforce-chat-support"
          src={`${config.siteEndpoint}/assets/js/bootstrap.min.js`}
          strategy="afterInteractive"
          onLoad={initEmbeddedMessaging}
          onError={(e) => {
            console.error("Failed to load Salesforce script:", e);
          }}
        />
      )}
      {children}
    </ContextProvider>
  );
};

SalesforceChatSupport.displayName = "SalesforceChatSupport";
